/* 360 turntable on a grid card — hover to spin, press and hold to scrub.
   Paired with snippets/card-stone-spin.liquid (which explains the zoom maths).

   ── WHY HOVER USED TO SHOW NOTHING, AND WHAT FIXED IT ────────────────────────
   The owner reported that stones with a 360 badge often did not start spinning.
   The cause is in the files themselves, measured 2026-07-30:

     * Stuller's clips are 1.4 – 10.5 MB, and
     * they are NOT faststart — `mdat` is first and `moov` is at the TAIL
       (verified: the first 2 KB of 174623546.mp4 contains `mdat`, no `moov`).

   A browser cannot decode a single frame until it has the `moov` atom. With
   `preload="none"` the first hover therefore had to open the file, discover the
   header was missing, range-request the tail, and only then start — 0.4 s on a
   10 Mbit desk connection for the small clips and ~3.8 s for a 10.5 MB one, far
   worse on mobile data. The old 6 s give-up timer expired first often enough that
   the feature looked broken.

   Two changes fix it:
     1. METADATA IS FETCHED WHEN THE CARD SCROLLS INTO VIEW, not on hover. That is
        the small header round-trip only (`preload="metadata"`), so by the time a
        cursor arrives the `moov` is known and play() is immediate. It is gated to
        `(hover: hover)` devices — a phone has no hover, so it must not pay for it.
     2. THE VIEWER IS REVEALED AS SOON AS ONE FRAME EXISTS (`loadeddata`,
        readyState >= 2), rather than waiting for playback to actually progress.

   ── DRAG ─────────────────────────────────────────────────────────────────────
   Press and hold now scrubs, as on GemsNY. This is only reasonable BECAUSE hover
   plays first: playing warms the buffer, so by the time a finger presses down the
   frames are local and scrubbing is smooth. A cold scrub on a non-faststart file
   would stutter, which is why drag is not offered before the clip has loaded.

   ⚠️ DRAG DIRECTION IS INVERTED RELATIVE TO TIME — the same trap already
   documented in snippets/stone-spin.liquid. The turntable rotates the stone one
   way as the clip advances, so ADDING the drag delta to currentTime makes the
   stone move opposite to the mouse. The owner reported exactly that on the
   product page. `frac` is SUBTRACTED here for the same reason; if it ever feels
   wrong again, flip the sign, not anything else.

   ⚠️ THE POINTER HANDLERS LIVE ON THE CARD'S MEDIA BOX, NOT ON THIS ELEMENT.
   The viewer layer is `pointer-events: none` so the card's <a> stays clickable
   (otherwise you hover a stone, watch it spin, click it and nothing happens).
   Listening on the parent keeps the link working AND gives us the events; a real
   drag then suppresses the click that would otherwise navigate mid-scrub.

   ⚠️ AND POINTER CAPTURE IS TAKEN ONLY ONCE A DRAG IS REAL — NEVER ON pointerdown.
   Capturing retargets the compatibility mouse events to the capture element, and
   the browser fires `click` at that element. The capture host is the media box and
   the product <a> sits INSIDE it, so a captured click lands on an ANCESTOR of the
   link and the event path never crosses it. Measured on a live card 2026-08-02:

     pointerdown -> target <img>,  <a> in path,  defaultPrevented false
     pointerup   -> target .card-product__media-inner,  <a> NOT in path
     click       -> target .card-product__media-inner,  <a> NOT in path, NOT prevented

   Nothing was suppressing the navigation. The link simply never heard the click.
   That is the owner's report — "sometimes it does press in, other times it just
   pauses the video": `onDown` bails out until the clip is armed with a known
   duration, so a press BEFORE the clip loads navigated and a press AFTER it loaded
   did not, which reads as random. Releasing the capture in `onUp` does not undo
   it either; the mouseup target is fixed when the event is dispatched, and the
   old code already released there.

   So capture is deferred to the first move past DRAG_SLOP — and so is the pause,
   which is the other half of the complaint. A plain press now leaves the clip
   spinning and lets the <a> do its job, with cmd-click, middle-click and
   open-in-new-tab all intact because the browser handles the click, not us. */
(function () {
  'use strict';

  var HOVER_DELAY = 90;      // ms the pointer must settle before play()
  // px of travel before a press counts as a drag rather than a click. 10, not the
  // 6 it was: a trackpad tap-to-click drifts several pixels under an ordinary
  // finger, and any drift past this line turns a shopper's click into a scrub —
  // whose click onClick() then deliberately suppresses, so the product page never
  // opens. This is the SECOND way a press failed to navigate; the capture
  // retargeting described in the header is the first.
  var DRAG_SLOP   = 10;
  var STALL_MS    = 12000;   // give-up budget; generous because the files are big
  var current = null;        // the one element allowed to be playing

  var canHover = !(window.matchMedia && window.matchMedia('(hover: none)').matches);
  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  var saveData = !!(conn && (conn.saveData || /^(slow-)?2g$/.test(conn.effectiveType || '')));

  // Warm only what is on screen, and only where a cursor can exist.
  var warmer = null;
  if (canHover && !saveData && 'IntersectionObserver' in window) {
    warmer = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.warm();
          warmer.unobserve(e.target);
        }
      });
    }, { rootMargin: '200px 0px' });
  }

  function stopCurrent(except) {
    if (current && current !== except) { current.stop(); current = null; }
  }

  customElements.get('stone-hover-spin') || customElements.define('stone-hover-spin',
    class extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('.shs__v');
        this.badge = this.querySelector('.shs__badge');
        this.src = this.dataset.src || '';
        this.armed = false;
        this.warmed = false;
        // "the shopper is on this card RIGHT NOW" — see reveal()
        this.wanted = false;
        this.dragging = false;
        this.moved = false;
        if (!this.video || !this.src) return;

        // A dead URL must not advertise itself.
        this.video.addEventListener('error', () => this.disable());

        // Reveal on the FIRST AVAILABLE FRAME. `loadeddata` fires at readyState 2,
        // well before `playing`, and it is the earliest moment there is anything
        // real to show. `playing` and a progressing `timeupdate` are kept as
        // belt-and-braces for engines that skip straight past loadeddata.
        var reveal = () => this.reveal();
        this.video.addEventListener('loadeddata', reveal);
        this.video.addEventListener('playing', reveal);
        this.video.addEventListener('timeupdate', () => {
          if (this.video.currentTime > 0) this.reveal();
        });

        // ---- pointer handling on the CARD, not on this layer (see header) ----
        this.host = this.closest('.card-product__media-inner') || this.parentElement;
        if (this.host) {
          this.host.addEventListener('pointerenter', this.onEnter);
          this.host.addEventListener('pointerleave', this.onLeave);
          this.host.addEventListener('pointerdown', this.onDown);
          this.host.addEventListener('pointermove', this.onMove);
          this.host.addEventListener('pointerup', this.onUp);
          this.host.addEventListener('pointercancel', this.onUp);
          // Capture phase: the click must be killed before the <a> sees it.
          this.host.addEventListener('click', this.onClick, true);
        }

        this.badge.addEventListener('click', this.onBadge);
        this.badge.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault(); e.stopPropagation(); this.onBadge(e);
          }
        });

        if (warmer) warmer.observe(this);
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        clearTimeout(this.stallTimer);
        if (warmer) warmer.unobserve(this);
        if (current === this) current = null;
      }

      disable() {
        this.classList.remove('is-loading', 'is-playing');
        this.classList.add('is-dead');
        if (this.badge) this.badge.hidden = true;
      }

      // Fetch the header only. This is what makes a later play() instant.
      warm() {
        if (this.warmed || this.armed) return;
        this.warmed = true;
        this.video.preload = 'metadata';
        this.video.src = this.src;
      }

      arm() {
        if (this.armed) return;
        this.armed = true;
        this.video.preload = 'auto';
        if (!this.video.src) this.video.src = this.src;
      }

      // ⚠️ WARMING MUST NEVER REVEAL. THIS GUARD IS THE WHOLE FEATURE.
      // `warm()` sets `src` when the card scrolls into view, and setting a src is
      // enough to fire `loadeddata` — so a card that had never been hovered called
      // reveal(), took `is-playing`, and faded its VIDEO FRAME in over the branded
      // still. The owner reported it exactly: "I saw the pictures for a second and
      // then the video preview."
      //
      // It is not a subtle difference on screen. Stuller's turntable leaves the
      // stone filling ~37% of the frame while our card still is normalised to 76%
      // (stone_image.TARGET_FILL), so a revealed video shows a small stone on a
      // white plate where the card should show a large branded one — and the
      // wordmark disappears with it.
      //
      // ⚠️ AND THE FLAG IS `wanted`, NOT `armed`. `armed` is sticky for the life of
      // the element — it goes true on the first hover and never comes back down —
      // so gating on it fixes the scroll-in reveal and leaves a second one: after
      // the cursor LEAVES, stop() removes `is-playing`, a trailing `timeupdate`
      // from the just-paused video calls reveal(), and the class goes straight back
      // on. Measured: the video was still showing after pointerleave. `wanted` is
      // set by start() and cleared by stop()/giveUp(), so a late event that arrives
      // after the shopper has moved on cannot resurrect the viewer.
      reveal() {
        if (!this.wanted) return;
        clearTimeout(this.stallTimer);
        this.classList.add('is-playing');
        this.classList.remove('is-loading');
      }

      giveUp() {
        this.wanted = false;
        this.classList.remove('is-loading');
        if (this.armed && !this.video.paused) this.video.pause();
        if (current === this) current = null;
      }

      start() {
        this.wanted = true;
        this.arm();
        stopCurrent(this);
        current = this;
        // ⚠️ A WARMED CLIP HAS ALREADY FIRED `loadeddata`, AND IT WILL NOT FIRE IT
        // AGAIN. Now that reveal() refuses to run unless armed, the reveal for a
        // warmed card can only come from `playing` — which is one more round trip,
        // and nothing at all if a browser ever refuses the play(). The frame is
        // already decoded and sitting there, so read readyState directly instead of
        // waiting for an event that has been and gone. This is also what makes the
        // first hover instant, which is the entire reason for warming.
        if (this.video.readyState >= 2) this.reveal();
        if (!this.classList.contains('is-playing')) this.classList.add('is-loading');
        clearTimeout(this.stallTimer);
        this.stallTimer = setTimeout(() => {
          if (!this.classList.contains('is-playing')) this.giveUp();
        }, STALL_MS);
        var p = this.video.play();
        if (p && p.catch) p.catch(() => { /* reveal() may still fire on loadeddata */ });
      }

      stop() {
        this.wanted = false;
        clearTimeout(this.timer);
        clearTimeout(this.stallTimer);
        this.classList.remove('is-loading', 'is-playing');
        if (this.armed) this.video.pause();
      }

      onEnter = (e) => {
        // A tap on a touch screen also fires pointerenter; it must not start a
        // multi-megabyte download on the connection least able to afford it.
        if (e.pointerType && e.pointerType !== 'mouse') return;
        if (reduceMotion || saveData) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.start(), HOVER_DELAY);
      };

      onLeave = () => {
        if (this.dragging) return;         // never yank it away mid-scrub
        clearTimeout(this.timer);
        this.stop();
        if (current === this) current = null;
      };

      /* ------------------------------------------------------------ drag ---- */
      onDown = (e) => {
        // FIRST, and before every early return: a `moved` left true by a gesture
        // that ended in `pointercancel` is never cleared by onClick, because no
        // click follows a cancel. It would then make the NEXT honest click
        // preventDefault itself into nothing — one more intermittent dead press.
        this.moved = false;
        if (e.target === this.badge || this.badge.contains(e.target)) return;
        // ⚠️ SCRUBBING IS MOUSE-ONLY, for the same reason hover is. The header's
        // bargain is that press-and-hold is only reasonable BECAUSE hover warmed
        // the buffer first — and a phone never hovers, so it never earns the drag.
        // Without this a touch card that had been armed by its badge would read a
        // diagonal SCROLL as a scrub: 10px of horizontal drift takes the pointer
        // capture and preventDefaults the moves, which hijacks the page scroll and
        // eats the tap. On touch the badge is the whole interaction (see
        // snippets/card-stone-spin.liquid), so a tap here is only ever a tap, and
        // a tap must reach the <a>.
        if (e.pointerType && e.pointerType !== 'mouse') return;
        if (!this.armed || !this.video.duration) return;   // nothing loaded to scrub
        this.dragging = true;
        this.captured = false;
        this.pressX = e.clientX;
        // NO pause and NO capture here — see the header. Until the pointer has
        // travelled DRAG_SLOP this is still a click, and a click must reach the <a>.
      };

      onMove = (e) => {
        if (!this.dragging) return;
        if (!this.moved) {
          if (Math.abs(e.clientX - this.pressX) < DRAG_SLOP) return;  // still a click
          // The gesture has become a drag. Take the clip, and only NOW the pointer.
          this.moved = true;
          this.video.pause();                              // hand over control
          this.classList.add('is-dragging');
          try { this.host.setPointerCapture(e.pointerId); this.captured = true; } catch (_) {}
          // Re-baseline both. The clip kept PLAYING through the press, so a
          // currentTime sampled at pointerdown is stale by however long the finger
          // rested, and the slop travel is not rotation the shopper asked for.
          // Baselining here is what stops the stone jumping as the drag takes hold.
          this.startX = e.clientX;
          this.startT = this.video.currentTime;
        }
        e.preventDefault();
        var d = this.video.duration;
        // One card-width of travel is one full rotation. SUBTRACTED — see header.
        var frac = (e.clientX - this.startX) / Math.max(1, this.host.clientWidth);
        var t = (this.startT - frac * d) % d;
        if (t < 0) t += d;
        this.video.currentTime = t;
      };

      onUp = (e) => {
        if (!this.dragging) return;
        this.dragging = false;
        this.classList.remove('is-dragging');
        // Only release what was actually taken — capture is now conditional.
        if (this.captured) {
          this.captured = false;
          try { this.host.releasePointerCapture(e.pointerId); } catch (_) {}
        }
        // Resume only if the cursor is still over the card; a drag that ended
        // off-card should leave it where the shopper put it. A press that never
        // became a drag never paused, so there is nothing to resume.
        if (this.moved && this.host.matches(':hover')) this.video.play().catch(() => {});
      };

      onClick = (e) => {
        // A drag ends in a click the browser would follow to the product page.
        if (this.moved) {
          e.preventDefault();
          e.stopPropagation();
          this.moved = false;
        }
      };

      onBadge = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.classList.contains('is-dead')) return;
        if (this.video.paused) this.start();
        else { this.stop(); if (current === this) current = null; }
      };
    });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stopCurrent(null);
  });
})();
